import { JSX } from 'react';
import {
    BooleanInput,
    Create,
    SimpleForm,
    TextInput,
    required,
    useNotify,
    useRedirect,
} from 'react-admin';
import { useWatch } from 'react-hook-form';
import { Typography } from '@mui/material';
import { AclResourceTreeInput } from '../edit/components/acl/AclResourceTreeInput';

/**
 * Creating an ACL role.
 *
 * Same shape as `AdminRoleEdit` and deliberately not shared with it: the two differ in what they
 * may show (no system-role notice here, no record to read) and in their defaults, and folding
 * them together would mean a conditional in every field for the sake of a dozen lines.
 */

const RoleFields: () => JSX.Element = (): JSX.Element => {
    const isAll: boolean = useWatch({ name: 'isAll' }) === true;

    return (
        <>
            <TextInput source="name" validate={ [required()] } fullWidth />
            <TextInput source="description" multiline fullWidth />
            <BooleanInput
                source="isAll"
                label="Access to everything"
                helperText="On, this role reaches every resource, including ones added later. Off, choose them below."
            />
            <Typography variant="subtitle2" sx={ { mt: 2 } }>Resource access</Typography>
            <AclResourceTreeInput source="resources" disabled={ isAll } />
        </>
    );
};

export const AdminRoleCreate: () => JSX.Element = (): JSX.Element => {
    const notify = useNotify();
    const redirect = useRedirect();

    return (
        <Create
            mutationOptions={ {
                onSuccess: (): void => {
                    notify('Role created', { type: 'info' });
                    redirect('list', 'admin_roles');
                },
                onError: (error: any): void => notify(
                    typeof error?.message === 'string' && error.message.length > 0
                        ? error.message
                        : 'Could not create the role',
                    { type: 'error' }
                ),
            } }
        >
            <SimpleForm defaultValues={ { isAll: false, resources: [] } }>
                <RoleFields />
            </SimpleForm>
        </Create>
    );
}
